/**
 * CurrentLocationService - Cross-viewport location synchronization with centerline awareness.
 *
 * Dual referential: accepts both world positions and centerline coordinates,
 * keeping them in sync. Owns the active centerline concept internally.
 *
 * Entry points:
 * - setCurrentWorldPosition(): resolves closest centerline via hysteresis
 * - setCurrentCenterlinePosition(): computes world position from centerline node
 *
 * Events:
 * - LOCATION_CHANGED: fired on every position update (world + optional centerline)
 * - ACTIVE_CENTERLINE_CHANGED: fired only when the active centerline switches
 */

import { PubSubService } from '../core';
import type { ServiceContext, ServiceRegistration } from '../core/types';
import type { PickResult } from '../display';
import type { Vec3 } from '../domain';
import { ViewportDataService } from './viewport-data-service';
import type { Centerline, ClosestPointResult } from '../domain/centerline';

export interface CenterlinePosition {
    centerlineId: string;
    nodeIndex: number;
    centerline: Centerline;
}

export interface CurrentLocation {
    /** World coordinates */
    worldPosition: Vec3;
    /** Resolved centerline position (null when no centerline is close) */
    centerlinePosition: CenterlinePosition | null;
    /** Source of the location change (for context) */
    source?: PickResult;
    /** Timestamp */
    timestamp: number;
}

/**
 * Hysteresis threshold in mm.
 * If the currently active centerline is within this distance,
 * it stays active even if another centerline is slightly closer.
 * This prevents jittery switching at vessel branch points.
 */
const CENTERLINE_HYSTERESIS_MM = 10.0;

/**
 * Minimum improvement in mm required to switch away from the current active centerline.
 */
const CENTERLINE_SWITCH_IMPROVEMENT_MM = 2.0;

export class CurrentLocationService extends PubSubService {
    static readonly SERVICE_NAME = 'CurrentLocationService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: CurrentLocationService.SERVICE_NAME,
        factory: (context: ServiceContext) => new CurrentLocationService(context),
    };

    readonly EVENTS = {
        LOCATION_CHANGED: 'location:changed',
        ACTIVE_CENTERLINE_CHANGED: 'location:active-centerline:changed',
    } as const;

    private currentLocation: CurrentLocation | null = null;
    private viewportDataService: ViewportDataService | null = null;
    private activeCenterline: Centerline | null = null;

    constructor(private readonly context: ServiceContext) {
        super();
    }

    /**
     * Lazily resolve ViewportDataService to handle service initialization order.
     */
    private getViewportDataService(): ViewportDataService | null {
        if (!this.viewportDataService) {
            try {
                this.viewportDataService = this.context.services.getService<ViewportDataService>(
                    ViewportDataService.SERVICE_NAME
                );
            } catch {
                // ViewportDataService not registered yet — graceful fallback
            }
        }
        return this.viewportDataService;
    }

    /**
     * Set location from world coordinates.
     * Resolves the closest centerline via hysteresis and broadcasts events.
     */
    setCurrentWorldPosition(worldPosition: Vec3, source?: PickResult): void {
        const previousCenterline = this.activeCenterline;
        const centerlinePosition = this.resolveActiveCenterline(worldPosition);

        this.currentLocation = {
            worldPosition,
            centerlinePosition,
            source,
            timestamp: Date.now(),
        };

        if (this.activeCenterline !== previousCenterline) {
            this.broadcast(this.EVENTS.ACTIVE_CENTERLINE_CHANGED, this.activeCenterline);
        }
        this.broadcast(this.EVENTS.LOCATION_CHANGED, this.currentLocation);
    }

    /**
     * Set location from centerline coordinates.
     * Computes the world position from the centerline node and broadcasts events.
     */
    setCurrentCenterlinePosition(centerlineId: string, nodeIndex: number, source?: PickResult): void {
        const dataService = this.getViewportDataService();
        if (!dataService) return;

        const centerlines = dataService.getSlot('centerlines');
        if (!centerlines) return;

        const centerline = centerlines.get(centerlineId);
        if (!centerline) return;

        const clampedIndex = Math.max(0, Math.min(nodeIndex, centerline.nodeCount - 1));
        const node = centerline.getNodeAt(clampedIndex);
        const previousCenterline = this.activeCenterline;

        this.activeCenterline = centerline;

        this.currentLocation = {
            worldPosition: node.point,
            centerlinePosition: { centerlineId, nodeIndex: clampedIndex, centerline },
            source,
            timestamp: Date.now(),
        };

        if (this.activeCenterline !== previousCenterline) {
            this.broadcast(this.EVENTS.ACTIVE_CENTERLINE_CHANGED, this.activeCenterline);
        }
        this.broadcast(this.EVENTS.LOCATION_CHANGED, this.currentLocation);
    }

    getLocation(): CurrentLocation | null {
        return this.currentLocation;
    }

    getActiveCenterline(): Centerline | null {
        return this.activeCenterline;
    }

    override reset(): void {
        this.currentLocation = null;
        this.viewportDataService = null;
        this.activeCenterline = null;
        super.reset();
    }

    // ========================================================================
    // Active centerline resolution
    // ========================================================================

    /**
     * Resolve the active centerline from a world position using hysteresis.
     * Updates this.activeCenterline and returns the resolved CenterlinePosition (or null).
     */
    private resolveActiveCenterline(worldPosition: Vec3): CenterlinePosition | null {
        const dataService = this.getViewportDataService();
        if (!dataService) return null;

        const centerlines = dataService.getSlot('centerlines');
        if (!centerlines || centerlines.size === 0) {
            this.activeCenterline = null;
            return null;
        }

        // Find closest point on each centerline
        const candidates: Array<{
            centerlineId: string;
            centerline: Centerline;
            result: ClosestPointResult;
        }> = [];
        for (const [id, centerline] of centerlines) {
            candidates.push({
                centerlineId: id,
                centerline,
                result: centerline.findClosestPoint(worldPosition),
            });
        }

        // Sort by distance (error)
        candidates.sort((a, b) => a.result.error - b.result.error);
        const closest = candidates[0];

        // Hysteresis: prefer current active if it's still close enough
        if (this.activeCenterline) {
            const currentResult = this.activeCenterline.findClosestPoint(worldPosition);

            if (
                currentResult.error < CENTERLINE_HYSTERESIS_MM &&
                currentResult.error - closest.result.error < CENTERLINE_SWITCH_IMPROVEMENT_MM
            ) {
                // Keep current active — it's still close enough
                return {
                    centerlineId: this.activeCenterline.id,
                    nodeIndex: currentResult.nodeIndex,
                    centerline: this.activeCenterline,
                };
            }
        }

        // Switch to closest centerline
        this.activeCenterline = closest.centerline;
        return {
            centerlineId: closest.centerlineId,
            nodeIndex: closest.result.nodeIndex,
            centerline: closest.centerline,
        };
    }
}
